import {
    Box,
    Flex,
    FormLabel,
    Image,
    Input,
    Link,
    Progress,
    Spinner,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import uploadIcon from "../../assets/images/upload-icon.svg";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { handleUploadFile } from "@features/upload/action";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@src/store";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updatePrintJob } from "@features/print/action";
import i18n from "i18next";
import { toast } from "react-toastify";

interface FileUploadStatus {
    id: string;
    file: File;
    status: "pending" | "uploading" | "uploaded" | "success" | "error";
    progress: number;
    printJobId?: number;
    errorKey?: "fileSizeTooBig" | "generic";
}

const Index = () => {
    const [uploads, setUploads] = useState<FileUploadStatus[]>([]);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const lang = i18n.language;
    const batchIdRef = useRef<string | null>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const newUploads: FileUploadStatus[] = Array.from(e.target.files).map(
            (file) => ({
                id: crypto.randomUUID(),
                file,
                status: "pending",
                progress: 0,
            })
        );

        batchIdRef.current = crypto.randomUUID();
        setUploads((prev) => [...prev, ...newUploads]);
    };

    const MAX_FILE_SIZE = 50 * 1024 * 1024;

    const uploadFile = async (upload: FileUploadStatus) => {
        if (upload.file.size > MAX_FILE_SIZE) {
            setUploads((prev) =>
                prev.map((u) =>
                    u.id === upload.id ? { ...u, status: "error", errorKey: "fileSizeTooBig" as const } : u
                )
            );
            return;
        }

        try {
            setUploads((prev) =>
                prev.map((u) =>
                    u.id === upload.id ? { ...u, status: "uploading" } : u
                )
            );

            const buffer = await upload.file.arrayBuffer();

            const result: any = await dispatch(
                handleUploadFile({
                    fileName: upload.file.name,
                    body: buffer,
                })
            );

            if (result.error) {
                const httpStatus = result.payload?.status ?? result.payload?.response?.status;
                throw { httpStatus };
            }

            setUploads((prev) =>
                prev.map((u) =>
                    u.id === upload.id
                        ? {
                            ...u,
                            status: "uploaded",
                            progress: 100,
                            printJobId: result.payload.ID
                          }
                        : u
                )
            );
        } catch (error: any) {
            const errorKey = error?.httpStatus === 413 ? "fileSizeTooBig" : "generic";
            setUploads((prev) =>
                prev.map((u) =>
                    u.id === upload.id ? { ...u, status: "error", errorKey } : u
                )
            );
        }
    };

    const CONCURRENCY_LIMIT = 2;

    useEffect(() => {
        const activeCount = uploads.filter((u) => u.status === "uploading").length;
        const slots = CONCURRENCY_LIMIT - activeCount;
        if (slots <= 0) return;

        uploads
            .filter((u) => u.status === "pending")
            .slice(0, slots)
            .forEach(uploadFile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploads]);

    useEffect(() => {
        if (uploads.length === 0 || !batchIdRef.current) return;

        const allUploaded = uploads.every(
            (u) => u.status === "uploaded" || u.status === "error"
        );

        if (!allUploaded) return;

        const uploaded = uploads.filter((u) => u.status === "uploaded");

        if (uploaded.length === 0) return;

        const batchUpdatePrintJobs = async () => {
            try {
                const body = uploaded.map((u) => ({
                    ID: u.printJobId,
                    PRINT_COLOR: 0,
                    PAGESIZE: 0,
                    PRINT_ORIENTATION: 0,
                    PRINT_DUPLEX: 0,
                    PRINT_COPIES: 0,
                    STATUS: 20,
                }));

                await dispatch(updatePrintJob({ body }));

                setUploads((prev) =>
                    prev.map((u) =>
                        u.status === "uploaded"
                            ? { ...u, status: "success", progress: 100 }
                            : u
                    )
                );
            } catch (error) {
                setUploads((prev) =>
                    prev.map((u) =>
                        u.status === "uploaded"
                            ? { ...u, status: "error" }
                            : u
                    )
                );
            }
        };

        batchUpdatePrintJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploads]);

    useEffect(() => {
        if (uploads.length === 0 || !batchIdRef.current) return;

        const allDone = uploads.every(
            (u) => u.status === "success" || u.status === "error"
        );

        if (!allDone) return;

        const succeeded = uploads.filter((u) => u.status === "success").length;
        const failed = uploads.filter((u) => u.status === "error").length;

        if (succeeded > 0) {
            toast.success(
                t("printManagement.upload.toast.successMsg")
            );
        }

        if (failed > 0) {
            toast.error(
                t("printManagement.upload.toast.errorMsg", {
                    count: failed,
                    defaultValue: `${failed} bestand(en) gefaald`,
                })
            );
        }

        if (succeeded > 0) {
            navigate(`/${lang}${t("pageRoutes.bevestig-upload")}`);
        }

        batchIdRef.current = null;
    }, [uploads, navigate, lang, t]);

    return (
        <>
            <Flex
                alignItems="center"
                justifyContent="center"
                width="full"
                padding={{ base: "20px", md: "32px", lg: "45px" }}
                border="1px"
                borderColor="custom.gray_300"
                position="relative"
            >
                <VStack spacing="18px" width="100%">
                    <Image src={uploadIcon} alt="upload icon" maxW="160px" />

                    <FormLabel textAlign="center" mb="0" width="100%">
                        <Input
                            type="file"
                            onChange={handleFileChange}
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            opacity="0"
                            cursor="pointer"
                            multiple
                        />

                        <Stack mb="30px">
                            <Link
                                color="custom.brand_primary"
                                fontWeight="semibold"
                                position="relative"
                                zIndex={1}
                            >
                                {t("uploadBox.title")}
                            </Link>
                            <Text>{t("uploadBox.description")}</Text>
                        </Stack>
                    </FormLabel>

                    <Text fontSize="14px" textAlign="center">
                        {t("uploadBox.supportedFileExt")}
                    </Text>

                    <VStack spacing={4} width="100%" maxW="480px">
                        {uploads.map((u) => (
                            <Box key={u.id} width="100%">
                                <Flex justify="space-between" mb={1}>
                                    <Text fontSize="sm">{u.file.name}</Text>

                                    {u.status === "uploading" && <Spinner size="sm" />}
                                    {u.status === "success" && (
                                        <Text color="green.500">✓</Text>
                                    )}
                                    {u.status === "error" && (
                                        <Text color="red.500">✕</Text>
                                    )}
                                </Flex>

                                <Progress
                                    size="sm"
                                    value={u.progress}
                                    isIndeterminate={u.status === "uploading"}
                                    colorScheme={
                                        u.status === "error" ? "red" : "brand"
                                    }
                                />
                                {u.status === "error" && (
                                    <Text fontSize="xs" color="red.500" mt={1}>
                                        {t(u.errorKey === "fileSizeTooBig"
                                            ? "printManagement.upload.toast.fileSizeTooBig"
                                            : "printManagement.upload.toast.errorMsg"
                                        )}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </VStack>
                </VStack>
            </Flex>
        </>
    );
};

export default Index;
